// Gameplay markers: KOTH hill volume, Defusal bomb sites and the bomb itself.
// Hills and bomb sites are built per map from the map file; the game drives the dynamic state.

import * as THREE from "three";
import { createBombModel } from "./models/equipment";
import type { BombModel } from "./models/equipment";
import { ACCENT, TEAM_A_COLOR, TEAM_B_COLOR } from "./materials/palette";
import { ZoneVolume } from "./zones";
import type { MapFileJson } from "./mapTypes";
import type { ObjectiveRenderer, Vec3Like } from "./types";

const BOMB_FUSE_SEC = 40;
const SITE_COLOR = ACCENT.safetyYellow;
const NEUTRAL_COLOR = "#dfe8ea";

function hillColor(owner: "a" | "b" | "none", contested: boolean): string {
  if (contested) return ACCENT.runnerRed;
  if (owner === "a") return TEAM_A_COLOR;
  if (owner === "b") return TEAM_B_COLOR;
  return NEUTRAL_COLOR;
}

interface SiteLabel {
  sprite: THREE.Sprite;
  material: THREE.SpriteMaterial;
  texture: THREE.CanvasTexture;
}

function makeSiteLabel(text: string): SiteLabel | null {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext("2d");
  if (ctx === null) return null;
  ctx.clearRect(0, 0, 128, 128);
  ctx.lineWidth = 10;
  ctx.strokeStyle = SITE_COLOR;
  ctx.fillStyle = "rgba(20,22,26,0.55)";
  ctx.beginPath();
  ctx.arc(64, 64, 54, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = "#f2f1ee";
  ctx.font = "700 76px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, 64, 68);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const material = new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false, toneMapped: false });
  const sprite = new THREE.Sprite(material);
  sprite.scale.set(2.2, 2.2, 1);
  sprite.renderOrder = 16;
  return { sprite, material, texture };
}

export class ObjectiveManager implements ObjectiveRenderer {
  readonly group = new THREE.Group();
  private hills: ZoneVolume[] = [];
  private sites: ZoneVolume[] = [];
  private labels: SiteLabel[] = [];
  private bomb: BombModel | null = null;
  private bombRing: ZoneVolume | null = null;
  private bombPlanted = false;
  private hillIndex: number | null = null;
  private previewIndex: number | null = null;
  private plantedAt = 0;
  private time = 0;

  constructor() {
    this.group.name = "objectives";
  }

  /** Builds the hill / bomb site markers of a map (replaces the previous ones). */
  setMap(map: MapFileJson): void {
    this.disposeMapObjects();
    for (const hill of map.hills) {
      const zone = new ZoneVolume(hill.min, hill.max, 4.5, `hill:${hill.name}`);
      zone.setStyle(NEUTRAL_COLOR, 0);
      zone.group.visible = false;
      this.hills.push(zone);
      this.group.add(zone.group);
    }
    for (const site of map.bombSites) {
      const zone = new ZoneVolume(site.min, site.max, 3.2, `site:${site.name}`);
      zone.setStyle(SITE_COLOR, 0);
      zone.group.visible = false;
      this.sites.push(zone);
      this.group.add(zone.group);
      const label = makeSiteLabel(site.name);
      if (label !== null) {
        label.sprite.position.set(
          (site.min[0] + site.max[0]) / 2,
          site.min[1] + 2.6,
          (site.min[2] + site.max[2]) / 2,
        );
        label.sprite.visible = false;
        this.labels.push(label);
        this.group.add(label.sprite);
      }
    }
  }

  setHill(index: number | null, owner: "a" | "b" | "none", contested: boolean): void {
    this.hillIndex = index;
    for (let i = 0; i < this.hills.length; i++) {
      const zone = this.hills[i];
      const active = index === i;
      zone.group.visible = active || this.previewIndex === i;
      if (active) zone.setStyle(hillColor(owner, contested), contested ? 0.7 : 0);
    }
  }

  setHillPreview(index: number | null): void {
    if (index === this.previewIndex) return;
    this.previewIndex = index;
    for (let i = 0; i < this.hills.length; i++) {
      const zone = this.hills[i];
      if (i === this.hillIndex) continue;
      zone.group.visible = i === index;
      if (i === index) zone.setStyle(SITE_COLOR, 0.5);
    }
  }

  setBombSitesVisible(visible: boolean): void {
    for (const z of this.sites) z.group.visible = visible;
    for (const l of this.labels) l.sprite.visible = visible;
  }

  setBomb(state: { pos: Vec3Like; planted: boolean } | null): void {
    if (state === null) {
      if (this.bomb !== null) this.bomb.root.visible = false;
      if (this.bombRing !== null) this.bombRing.group.visible = false;
      this.bombPlanted = false;
      return;
    }
    if (this.bomb === null) {
      this.bomb = createBombModel();
      this.group.add(this.bomb.root);
    }
    if (this.bombRing === null) {
      this.bombRing = new ZoneVolume([-1.3, 0, -1.3], [1.3, 0, 1.3], 0.5, "bombRing");
      this.bombRing.setStyle(ACCENT.runnerRed, 0.9);
      this.group.add(this.bombRing.group);
    }
    if (state.planted && !this.bombPlanted) this.plantedAt = this.time;
    this.bombPlanted = state.planted;
    this.bomb.root.visible = true;
    this.bomb.root.position.set(state.pos.x, state.pos.y, state.pos.z);
    this.bombRing.group.visible = state.planted;
    this.bombRing.group.position.set(state.pos.x, state.pos.y, state.pos.z);
  }

  update(dt: number): void {
    this.time += dt;
    if (this.bomb !== null && this.bomb.root.visible) {
      const fraction = this.bombPlanted ? Math.min((this.time - this.plantedAt) / BOMB_FUSE_SEC, 1) : 0;
      this.bomb.update(this.bombPlanted ? "planted" : "idle", dt, fraction);
    }
  }

  /** Hides every dynamic marker (hill, bomb, bomb sites) but keeps the map-built objects. */
  clear(): void {
    this.setHillPreview(null);
    this.setHill(null, "none", false);
    this.setBomb(null);
    this.setBombSitesVisible(false);
  }

  private disposeMapObjects(): void {
    for (const z of this.hills) z.dispose();
    for (const z of this.sites) z.dispose();
    for (const l of this.labels) {
      l.sprite.removeFromParent();
      l.material.dispose();
      l.texture.dispose();
    }
    this.hills = [];
    this.sites = [];
    this.labels = [];
  }

  dispose(): void {
    this.disposeMapObjects();
    if (this.bomb !== null) {
      this.bomb.dispose();
      this.bomb = null;
    }
    if (this.bombRing !== null) {
      this.bombRing.dispose();
      this.bombRing = null;
    }
    this.group.removeFromParent();
  }
}
